import { useRef, useState, DragEvent, ChangeEvent } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadMedia, type MediaKind } from '@/services/storage';
import { AlertModal } from './AlertModal';

const ERROR_TITLES: Record<MediaKind, string> = {
  image: 'Não foi possível enviar a imagem',
  product: 'Não foi possível enviar a imagem do produto',
  logo: 'Não foi possível enviar o logo',
  video: 'Não foi possível enviar o vídeo',
};

interface Props {
  kind: MediaKind;
  value?: string;
  onChange: (url: string) => void;
  className?: string;
  /** dica visual: texto curto mostrado quando vazio */
  hint?: string;
}

const ACCEPT: Record<MediaKind, string> = {
  image: 'image/png,image/jpeg,image/webp,image/gif',
  product: 'image/png,image/jpeg,image/webp',
  logo: 'image/png,image/jpeg,image/webp,image/svg+xml',
  video: 'video/mp4,video/webm,video/x-matroska',
};

const SIZE_LABEL: Record<MediaKind, string> = {
  image: '10 MB',
  product: '5 MB',
  logo: '5 MB',
  video: '30 MB',
};

export function MediaUploader({ kind, value, onChange, className, hint }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [percent, setPercent] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    setPercent(0);
    try {
      const url = await uploadMedia(file, kind, (p) => setPercent(p.percent));
      onChange(url);
      toast.success('Arquivo enviado');
    } catch (e: any) {
      setErrorMessage(e?.message ?? 'Falha ao enviar arquivo.');
    } finally {
      setUploading(false);
      setPercent(0);
    }
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }

  const isVideo = kind === 'video';

  return (
    <div className={className}>
      {value ? (
        <div className="relative rounded-md border border-input bg-muted/30 overflow-hidden">
          {isVideo ? (
            <video src={value} className="w-full max-h-64 object-contain bg-black" controls muted />
          ) : (
            <img src={value} className="w-full max-h-64 object-contain bg-black/5" />
          )}
          <button
            type="button"
            onClick={() => onChange('')}
            className="absolute top-2 right-2 rounded-full bg-black/60 hover:bg-black/80 text-white p-1.5"
            title="Remover"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="absolute bottom-2 right-2 rounded-md bg-black/60 hover:bg-black/80 text-white text-xs px-2 py-1"
          >
            Trocar
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`rounded-md border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
            dragActive ? 'border-brand-500 bg-brand-50' : 'border-input hover:border-brand-300 hover:bg-muted/30'
          } ${uploading ? 'pointer-events-none opacity-70' : ''}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
              <div className="text-sm text-muted-foreground">
                {percent < 25 ? 'Otimizando…' : `Enviando… ${percent}%`}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              <Upload className="h-7 w-7 text-muted-foreground" />
              <div className="text-sm">
                <span className="font-semibold text-brand-700">Clique para enviar</span> ou arraste o arquivo
              </div>
              <div className="text-xs text-muted-foreground">
                {hint ?? (
                  isVideo
                    ? `MP4 H.264, até 1080p e ${SIZE_LABEL[kind]}`
                    : `PNG, JPG, WebP até ${SIZE_LABEL[kind]}`
                )}
              </div>
            </div>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT[kind]}
        className="hidden"
        onChange={onInputChange}
      />
      <AlertModal
        open={errorMessage !== null}
        onOpenChange={(o) => { if (!o) setErrorMessage(null); }}
        variant="error"
        title={ERROR_TITLES[kind]}
        description={errorMessage ?? undefined}
      />
    </div>
  );
}
